import { useState } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { BusinessRegistrationForm } from '@/types/businessRegistration';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface LocationConfirmationStepProps {
  formData: BusinessRegistrationForm;
  setFormData: (data: BusinessRegistrationForm) => void;
  onNext: () => void;
  onBack: () => void;
  isLoading?: boolean;
  errors?: { [key: string]: string };
}

export function LocationConfirmationStep({
  formData,
  setFormData,
  onNext,
  onBack,
  isLoading = false,
  errors = {}
}: LocationConfirmationStepProps) {
  const [isMapReady, setIsMapReady] = useState(false);
  const [isLocationConfirmed, setIsLocationConfirmed] = useState(false);
  const [showConfirmationAlert, setShowConfirmationAlert] = useState(false);

  const coordinates = formData.businessData.location.coordinates.coordinates;
  const address = formData.businessData.location.address;

  const handleMapReady = () => {
    setIsMapReady(true);
  };

  const handleLocationConfirm = () => {
    setIsLocationConfirmed(true);
    setShowConfirmationAlert(true);
    // Hide the alert after 3 seconds
    setTimeout(() => setShowConfirmationAlert(false), 3000);
  };

  const handleLocationAdjust = () => {
    setIsLocationConfirmed(false);
  };

  function MapEventHandler() {
    useMapEvents({
      click: (e) => {
        const { lat, lng } = e.latlng;
        setFormData({
          ...formData,
          businessData: {
            ...formData.businessData,
            location: {
              ...formData.businessData.location,
              coordinates: {
                type: 'Point',
                coordinates: [lng, lat]
              }
            }
          }
        });
        setIsLocationConfirmed(false);
      }
    });
    return null;
  }

  const getBusinessIcon = () => {
    const html = `
      <div class="flex items-center justify-center bg-white rounded-full p-2 shadow-lg border-2 border-[#77A146] hover:scale-110 transition-transform duration-300">
        <span class="text-2xl">${formData.businessData.emoji || '🏞️'}</span>
      </div>
    `;
  
    return L.divIcon({
      html,
      className: 'custom-div-icon',
      iconSize: L.point(60, 60),
      iconAnchor: L.point(30, 30),
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="space-y-6"
    >
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Confirm Your Location</h3>
        <p className="text-sm text-gray-500">
          Please verify that the location shown on the map is correct. You can click on the map to adjust the marker if needed.
        </p>

        {Object.keys(errors).length > 0 && (
          <Alert variant="destructive" className="my-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Please resolve the following issues:</AlertTitle>
            <AlertDescription>
              <ul className="list-disc pl-5 space-y-1">
                {Object.entries(errors).map(([key, message]) => (
                  <li key={key}>{message}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {showConfirmationAlert && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-700">
              Location confirmed! You can proceed to the next step.
            </AlertDescription>
          </Alert>
        )}

        <Card className="p-4 border-2 border-gray-200">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-primary mt-1" />
              <div>
                <h4 className="font-medium text-gray-900">Inferred Address</h4>
                <p className="text-sm text-gray-600">
                  {[
                    address.street,
                    address.city,
                    address.state,
                    address.country,
                    address.postalCode
                  ].filter(Boolean).join(', ')}
                </p>
              </div>
            </div>

            <div className="h-[400px] rounded-lg overflow-hidden border border-gray-200">
              <MapContainer
                center={[coordinates[1], coordinates[0]]}
                zoom={13}
                style={{ height: '100%', width: '100%' }}
                zoomControl={false}
                whenReady={handleMapReady}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <MapEventHandler />
                {isMapReady && (
                  <Marker
                    position={[coordinates[1], coordinates[0]]}
                    icon={getBusinessIcon()}
                  />
                )}
              </MapContainer>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-gray-400" />
                <p className="text-sm text-gray-500">
                  Click on the map to adjust the marker if needed
                </p>
              </div>
              <div className="flex gap-2">
                {!isLocationConfirmed ? (
                  <Button
                    onClick={handleLocationConfirm}
                    className="bg-primary hover:bg-[#1F4220]"
                    disabled={isLoading}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Confirm Location
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={handleLocationAdjust}
                    disabled={isLoading}
                  >
                    Adjust Location
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={onBack}
          disabled={isLoading}
        >
          Back
        </Button>
        <Button 
          onClick={onNext}
          disabled={isLoading || !isLocationConfirmed}
          className="min-w-[100px]"
        >
          Next
        </Button>
      </div>
    </motion.div>
  );
} 